import type { CampaignStep, CampaignStepKind } from '@barel/api-client/react';

export const CAMPAIGN_PLACEHOLDERS = {
  firstName: '{{firstName}}',
  company: '{{company}}',
} as const;

export interface CampaignMessageStepValue {
  enabled: boolean;
  delayDays: number;
  messageText: string;
}

export interface CampaignMessageSequenceValue {
  invit: CampaignMessageStepValue;
  fu1: CampaignMessageStepValue;
  fu2: CampaignMessageStepValue;
  fu3: CampaignMessageStepValue;
}

export type CampaignSequenceValidationCode =
  | 'required_followup'
  | 'non_monotonic_delays'
  | 'unsupported_placeholder'
  | 'invite_rendered_too_long';

export interface CampaignSequenceValidation {
  valid: boolean;
  codes: CampaignSequenceValidationCode[];
}

const PLACEHOLDER_ALIASES: readonly (readonly [RegExp, string])[] = [
  [/\{\{?\s*firstname\s*\}\}?/gi, CAMPAIGN_PLACEHOLDERS.firstName],
  [/\{\{?\s*first_name\s*\}\}?/gi, CAMPAIGN_PLACEHOLDERS.firstName],
  [/\{\{?\s*company\s*\}\}?/gi, CAMPAIGN_PLACEHOLDERS.company],
];

const MAX_RENDERED_PLACEHOLDER_LENGTH: Record<keyof typeof CAMPAIGN_PLACEHOLDERS, number> = {
  firstName: 64,
  company: 128,
};

/** Normalize legacy placeholder spellings to the single campaign grammar. */
export function canonicalizeCampaignPlaceholders(message: string): string {
  return PLACEHOLDER_ALIASES.reduce(
    (current, [pattern, replacement]) => current.replace(pattern, replacement),
    message,
  );
}

/** True when brace syntax remains after known placeholders are removed. */
export function hasUnsupportedCampaignPlaceholder(message: string): boolean {
  const withoutKnown = message
    .replaceAll(CAMPAIGN_PLACEHOLDERS.firstName, '')
    .replaceAll(CAMPAIGN_PLACEHOLDERS.company, '');
  return /[{}]/.test(withoutKnown);
}

export function renderCampaignMessagePreview(
  message: string,
  values: { firstName: string; company: string },
): string {
  return canonicalizeCampaignPlaceholders(message)
    .replaceAll(CAMPAIGN_PLACEHOLDERS.firstName, values.firstName)
    .replaceAll(CAMPAIGN_PLACEHOLDERS.company, values.company);
}

/** Conservative upper bound used to keep LinkedIn invitation notes <= 300 chars after rendering. */
export function maxRenderedCampaignMessageLength(message: string): number {
  const canonical = canonicalizeCampaignPlaceholders(message);
  return canonical
    .replaceAll(
      CAMPAIGN_PLACEHOLDERS.firstName,
      'x'.repeat(MAX_RENDERED_PLACEHOLDER_LENGTH.firstName),
    )
    .replaceAll(CAMPAIGN_PLACEHOLDERS.company, 'x'.repeat(MAX_RENDERED_PLACEHOLDER_LENGTH.company))
    .length;
}

function validateStepValues(
  steps: readonly {
    stepKind: CampaignStepKind;
    enabled: boolean;
    delayDays: number;
    messageText: string;
  }[],
): CampaignSequenceValidation {
  const codes = new Set<CampaignSequenceValidationCode>();
  const followUps = steps.filter((step) => step.stepKind !== 'invite' && step.enabled);

  if (followUps.some((step) => step.messageText.trim().length === 0)) {
    codes.add('required_followup');
  }

  for (let index = 1; index < followUps.length; index += 1) {
    const previous = followUps[index - 1];
    const current = followUps[index];
    if (previous && current && current.delayDays <= previous.delayDays) {
      codes.add('non_monotonic_delays');
      break;
    }
  }

  if (
    steps.some((step) =>
      hasUnsupportedCampaignPlaceholder(canonicalizeCampaignPlaceholders(step.messageText)),
    )
  ) {
    codes.add('unsupported_placeholder');
  }

  const invite = steps.find((step) => step.stepKind === 'invite');
  if (invite?.messageText && maxRenderedCampaignMessageLength(invite.messageText) > 300) {
    codes.add('invite_rendered_too_long');
  }

  return { valid: codes.size === 0, codes: Array.from(codes) };
}

export function validateCampaignMessageSequence(
  sequence: CampaignMessageSequenceValue,
): CampaignSequenceValidation {
  return validateStepValues([
    { stepKind: 'invite', ...sequence.invit },
    { stepKind: 'fu1', ...sequence.fu1 },
    { stepKind: 'fu2', ...sequence.fu2 },
    { stepKind: 'fu3', ...sequence.fu3 },
  ]);
}

export function validateCampaignSteps(
  steps: readonly Pick<CampaignStep, 'stepKind' | 'enabled' | 'delayDays' | 'messageText'>[],
): CampaignSequenceValidation {
  const order: Record<CampaignStepKind, number> = { invite: 0, fu1: 1, fu2: 2, fu3: 3 };
  return validateStepValues([...steps].sort((a, b) => order[a.stepKind] - order[b.stepKind]));
}
